/**
 * FikoSanalSensor.ts
 *
 * Virtual sensor element for SPH fluid simulation.
 * Monitors physical properties (pressure, velocity, temperature, etc.) within a spherical region.
 *
 * GPU Memory Layout: 32 bytes
 * ================================
 * Offset | Size | Field
 * -------|------|------------------
 * 0      | 4    | pozX (f32)
 * 4      | 4    | pozY (f32)
 * 8      | 4    | pozZ (f32)
 * 12     | 4    | yaricap (f32)
 * 16     | 4    | tip (u32)
 * 20     | 4    | orneklemFrekansi (u32)
 * 24     | 4    | pad
 * 28     | 4    | pad
 * ================================
 */

import { ISPHEleman, ElemanTipi, olusturId } from './ISPHEleman';

/**
 * Sensor measurement types
 */
export enum SensorTipi {
  BASINC = 0,      // Pressure measurement
  HIZ = 1,         // Velocity magnitude
  SICAKLIK = 2,    // Temperature
  DOLUM = 3,       // Fill level / particle density
  DEBI = 4,        // Flow rate
  YOGUNLUK = 5,    // Density
  TURBLANS = 6     // Turbulence intensity
}

/**
 * Sensor measurement data point
 */
export interface SensorOlcum {
  zaman: number;   // Simulation time
  deger: number;   // Measured value
}

/**
 * Configuration interface for sensor
 */
export interface SanalSensorConfig {
  pozisyon?: { x: number; y: number; z: number };
  olcumYaricapi?: number;
  sensorTipi?: SensorTipi;
  orneklemeFrekansi?: number;
  grafikGoster?: boolean;
  kaydet?: boolean;
  alarmEsik?: number | null;
  ad?: string;
  aktif?: boolean;
}

/**
 * Virtual sensor element for monitoring fluid properties
 *
 * This element samples physical properties within a spherical region
 * at specified intervals. Can be used for:
 * - Real-time monitoring and visualization
 * - Data logging and export
 * - Alarm triggering based on thresholds
 * - Validation against physical sensors
 */
export class FikoSanalSensor implements ISPHEleman {
  readonly tip = ElemanTipi.SANAL_SENSOR;
  readonly id: string;

  // Spatial properties
  pozisyon: { x: number; y: number; z: number };
  olcumYaricapi: number;

  // Sensor configuration
  sensorTipi: SensorTipi;
  orneklemeFrekansi: number;  // Samples per simulation time unit

  // Display and logging
  grafikGoster: boolean;      // Show real-time graph
  kaydet: boolean;            // Enable data recording
  alarmEsik: number | null;   // Threshold for alarm trigger

  // Metadata
  ad: string;
  aktif: boolean;

  // Internal data storage
  private olcumVerileri: SensorOlcum[] = [];
  private sonOrneklemZamani: number = 0;
  private maksVeriSayisi: number = 10000;  // Prevent memory overflow

  constructor(config: SanalSensorConfig = {}) {
    this.id = olusturId();
    this.pozisyon = config.pozisyon || { x: 0, y: 0, z: 0 };
    this.olcumYaricapi = config.olcumYaricapi ?? 0.05;
    this.sensorTipi = config.sensorTipi ?? SensorTipi.BASINC;
    this.orneklemeFrekansi = config.orneklemeFrekansi ?? 1;
    this.grafikGoster = config.grafikGoster ?? true;
    this.kaydet = config.kaydet ?? false;
    this.alarmEsik = config.alarmEsik ?? null;
    this.ad = config.ad || `Sensor_${this.id.substring(0, 6)}`;
    this.aktif = config.aktif ?? true;
  }

  /**
   * Convert sensor data to GPU-compatible byte array (32 bytes)
   */
  toGPUBytes(): ArrayBuffer {
    const buffer = new ArrayBuffer(32);
    const view = new DataView(buffer);

    // Position (12 bytes)
    view.setFloat32(0, this.pozisyon.x, true);
    view.setFloat32(4, this.pozisyon.y, true);
    view.setFloat32(8, this.pozisyon.z, true);

    // Radius (4 bytes)
    view.setFloat32(12, this.olcumYaricapi, true);

    // Sensor type (4 bytes)
    view.setUint32(16, this.sensorTipi, true);

    // Sampling frequency (4 bytes)
    view.setUint32(20, this.orneklemeFrekansi, true);

    // Padding (8 bytes) - leave as zeros

    return buffer;
  }

  /**
   * GPU struct byte size for this element
   */
  gpuStructBoyut(): number {
    return 32;
  }

  /**
   * Add a measurement data point
   * @param zaman - Simulation time
   * @param deger - Measured value
   */
  veriEkle(zaman: number, deger: number): void {
    if (!this.kaydet) return;

    // Check sampling frequency
    const orneklemAraliği = 1.0 / this.orneklemeFrekansi;
    if (zaman - this.sonOrneklemZamani < orneklemAraliği) {
      return;
    }

    this.olcumVerileri.push({ zaman, deger });
    this.sonOrneklemZamani = zaman;

    // Prevent memory overflow - keep most recent data
    if (this.olcumVerileri.length > this.maksVeriSayisi) {
      this.olcumVerileri.shift();
    }

    // Check alarm threshold
    if (this.alarmEsik !== null && Math.abs(deger) > this.alarmEsik) {
      console.warn(`[${this.ad}] Alarm: Value ${deger.toFixed(3)} exceeds threshold ${this.alarmEsik}`);
    }
  }

  /**
   * Get all recorded measurement data
   */
  veriGetir(): SensorOlcum[] {
    return [...this.olcumVerileri];
  }

  /**
   * Get the most recent measurement value
   */
  sonDeger(): number {
    if (this.olcumVerileri.length === 0) return 0;
    return this.olcumVerileri[this.olcumVerileri.length - 1].deger;
  }

  /**
   * Clear all recorded data
   */
  veriTemizle(): void {
    this.olcumVerileri = [];
    this.sonOrneklemZamani = 0;
  }

  /**
   * Export data as CSV string
   */
  exportCSV(): string {
    const sensorTipiAdi = SensorTipi[this.sensorTipi];
    let csv = `# Sensor: ${this.ad} (${sensorTipiAdi})\n`;
    csv += `# Position: (${this.pozisyon.x}, ${this.pozisyon.y}, ${this.pozisyon.z})\n`;
    csv += `# Measurement Radius: ${this.olcumYaricapi}\n`;
    csv += `Zaman,Deger\n`;

    for (const olcum of this.olcumVerileri) {
      csv += `${olcum.zaman.toFixed(6)},${olcum.deger.toFixed(6)}\n`;
    }

    return csv;
  }

  /**
   * Export data as JSON string
   */
  exportJSON(): string {
    const data = {
      sensor: {
        id: this.id,
        ad: this.ad,
        tip: SensorTipi[this.sensorTipi],
        pozisyon: this.pozisyon,
        olcumYaricapi: this.olcumYaricapi
      },
      olcumler: this.olcumVerileri
    };
    return JSON.stringify(data, null, 2);
  }

  /**
   * Validate sensor configuration
   */
  dogrula(): { gecerli: boolean; hatalar: string[] } {
    const hatalar: string[] = [];

    if (this.olcumYaricapi <= 0) {
      hatalar.push('Ölçüm yarıçapı pozitif olmalıdır');
    }

    if (this.olcumYaricapi > 1.0) {
      hatalar.push('Ölçüm yarıçapı çok büyük (>1.0)');
    }

    if (this.orneklemeFrekansi <= 0) {
      hatalar.push('Örnekleme frekansı pozitif olmalıdır');
    }

    if (this.orneklemeFrekansi > 1000) {
      hatalar.push('Örnekleme frekansı çok yüksek (>1000)');
    }

    if (!Object.values(SensorTipi).includes(this.sensorTipi)) {
      hatalar.push('Geçersiz sensor tipi');
    }

    if (this.alarmEsik !== null && this.alarmEsik < 0) {
      hatalar.push('Alarm eşiği negatif olamaz');
    }

    return {
      gecerli: hatalar.length === 0,
      hatalar
    };
  }

  /**
   * Create a deep clone of this sensor
   */
  klonla(): FikoSanalSensor {
    const yeni = new FikoSanalSensor({
      pozisyon: { ...this.pozisyon },
      olcumYaricapi: this.olcumYaricapi,
      sensorTipi: this.sensorTipi,
      orneklemeFrekansi: this.orneklemeFrekansi,
      grafikGoster: this.grafikGoster,
      kaydet: this.kaydet,
      alarmEsik: this.alarmEsik,
      ad: `${this.ad}_kopya`,
      aktif: this.aktif
    });

    // Copy measurement data
    yeni.olcumVerileri = this.olcumVerileri.map(o => ({ ...o }));
    yeni.sonOrneklemZamani = this.sonOrneklemZamani;

    return yeni;
  }

  /**
   * Serialize to JSON
   */
  toJSON(): any {
    return {
      tip: 'SENSOR',
      id: this.id,
      pozisyon: this.pozisyon,
      olcumYaricapi: this.olcumYaricapi,
      sensorTipi: SensorTipi[this.sensorTipi],
      orneklemeFrekansi: this.orneklemeFrekansi,
      grafikGoster: this.grafikGoster,
      kaydet: this.kaydet,
      alarmEsik: this.alarmEsik,
      ad: this.ad,
      aktif: this.aktif,
      veriSayisi: this.olcumVerileri.length
    };
  }

  /**
   * Get list of adjustable parameters
   */
  parametreListesi(): Array<{ ad: string; tip: string; deger: any }> {
    return [
      { ad: 'pozisyon.x', tip: 'number', deger: this.pozisyon.x },
      { ad: 'pozisyon.y', tip: 'number', deger: this.pozisyon.y },
      { ad: 'pozisyon.z', tip: 'number', deger: this.pozisyon.z },
      { ad: 'olcumYaricapi', tip: 'number', deger: this.olcumYaricapi },
      { ad: 'sensorTipi', tip: 'enum', deger: SensorTipi[this.sensorTipi] },
      { ad: 'orneklemeFrekansi', tip: 'number', deger: this.orneklemeFrekansi },
      { ad: 'grafikGoster', tip: 'boolean', deger: this.grafikGoster },
      { ad: 'kaydet', tip: 'boolean', deger: this.kaydet },
      { ad: 'alarmEsik', tip: 'number', deger: this.alarmEsik },
      { ad: 'ad', tip: 'string', deger: this.ad },
      { ad: 'aktif', tip: 'boolean', deger: this.aktif }
    ];
  }

  /**
   * Set a parameter value
   */
  parametreAyarla(parametreAdi: string, deger: any): boolean {
    try {
      switch (parametreAdi) {
        case 'pozisyon.x':
          this.pozisyon.x = Number(deger);
          return true;
        case 'pozisyon.y':
          this.pozisyon.y = Number(deger);
          return true;
        case 'pozisyon.z':
          this.pozisyon.z = Number(deger);
          return true;
        case 'olcumYaricapi':
          this.olcumYaricapi = Math.max(0.001, Number(deger));
          return true;
        case 'sensorTipi':
          if (typeof deger === 'string') {
            this.sensorTipi = SensorTipi[deger as keyof typeof SensorTipi];
          } else {
            this.sensorTipi = Number(deger);
          }
          return true;
        case 'orneklemeFrekansi':
          this.orneklemeFrekansi = Math.max(0.1, Number(deger));
          return true;
        case 'grafikGoster':
          this.grafikGoster = Boolean(deger);
          return true;
        case 'kaydet':
          this.kaydet = Boolean(deger);
          return true;
        case 'alarmEsik':
          this.alarmEsik = deger === null ? null : Number(deger);
          return true;
        case 'ad':
          this.ad = String(deger);
          return true;
        case 'aktif':
          this.aktif = Boolean(deger);
          return true;
        default:
          return false;
      }
    } catch (e) {
      console.error(`Parametre ayarlama hatası: ${parametreAdi}`, e);
      return false;
    }
  }

  /**
   * Get a parameter value by name
   */
  parametreAl(ad: string): any {
    return (this as any)[ad];
  }
}

/**
 * Factory function to create a sensor with default values
 */
export function varsayilanSanalSensor(config: Partial<SanalSensorConfig> = {}): FikoSanalSensor {
  return new FikoSanalSensor(config);
}

/**
 * Helper function to get sensor type name
 */
export function sensorTipiAdi(tip: SensorTipi): string {
  const adlar: Record<SensorTipi, string> = {
    [SensorTipi.BASINC]: 'Basınç',
    [SensorTipi.HIZ]: 'Hız',
    [SensorTipi.SICAKLIK]: 'Sıcaklık',
    [SensorTipi.DOLUM]: 'Dolum',
    [SensorTipi.DEBI]: 'Debi',
    [SensorTipi.YOGUNLUK]: 'Yoğunluk',
    [SensorTipi.TURBLANS]: 'Türbülans'
  };
  return adlar[tip] || 'Bilinmeyen';
}

/**
 * Helper function to get sensor type unit
 */
export function sensorTipiBirim(tip: SensorTipi): string {
  const birimler: Record<SensorTipi, string> = {
    [SensorTipi.BASINC]: 'Pa',
    [SensorTipi.HIZ]: 'm/s',
    [SensorTipi.SICAKLIK]: '°C',
    [SensorTipi.DOLUM]: '%',
    [SensorTipi.DEBI]: 'L/s',
    [SensorTipi.YOGUNLUK]: 'kg/m³',
    [SensorTipi.TURBLANS]: 'm²/s³'
  };
  return birimler[tip] || '';
}
